import {
  Button,
  Caption1,
  Divider,
  Field,
  Input,
  Subtitle1,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  Text,
  Spinner,
  Title1
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { useForm } from "react-hook-form";
import authService from "../services/authService";
import { useState, useEffect } from "react";
import { useAppContext } from "../AppContext";
import { generateTwoFactorCode, requestEmailToken, resetPasswordRequest } from "../services/usersService";
import SupportDrawer from "./drawers/SupportDrawer";

type LoginFormValues = {
  email: string;
  password: string;
};

type TwoFactorFormValues = {
  code: string;
};

type EmailFormValues = {
  email: string;
};

const Login = () => {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<LoginFormValues>();
  const { register: registerTwoFactor, handleSubmit: handleSubmitTwoFactor, formState: { errors: twoFactorErrors, isSubmitting: isSubmittingTwoFactor } } = useForm<TwoFactorFormValues>();
  const { register: registerAccountRequest, handleSubmit: handleSubmitAccountRequest, formState: { errors: accountRequestErrors, isSubmitting: isSubmittingAccountRequest }, reset: resetAccountRequest } = useForm<EmailFormValues>();
  const { register: registerPasswordReset, handleSubmit: handleSubmitPasswordReset, formState: { errors: passwordResetErrors, isSubmitting: isSubmittingPasswordReset } } = useForm<EmailFormValues>();
  
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loggedUsers, setLoggedUsers] = useState<string>(localStorage.getItem("loggedUsers") || "");
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [showTwoFactorDialog, setShowTwoFactorDialog] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState<string>("1");
  const [twoFactorStep, setTwoFactorStep] = useState<"method" | "code">("method");
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [viewState, setViewState] = useState<"login" | "accountRequest" | "passwordReset">("login");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [accountRequestSent, setAccountRequestSent] = useState<boolean>(false);
  const [supportDrawerOpen, setSupportDrawerOpen] = useState<boolean>(false);
  const { setUser } = useAppContext();

  const approvedDomains = [
    'skaneskommuner.se',
    'almhult.se',
    'alvesta.se',
    'astorp.se',
    'attendo.se',
    'bastad.se',
    'bjuv.se',
    'bromolla.se',
    'burlov.se',
    'engelholm.se',
    'eslov.se',
    'falkenberg.se',
    'halmstad.se',
    'hassleholm.se',
    'helsingborg.se',
    'hoganas.se',
    'hoor.se',
    'horby.se',
    'hylte.se',
    'kavlinge.se',
    'klippan.se',
    'kristianstad.se',
    'laholm.se',
    'landskrona.se',
    'lessebo.se',
    'ljungby.se',
    'lomma.se',
    'lund.se',
    'malmo.se',
    'markaryd.se',
    'orkelljunga.se',
    'osby.se',
    'ostragoinge.se',
    'perstorp.se',
    'simrishamn.se',
    'sjobo.se',
    'skurup.se',
    'staffanstorp.se',
    'svalov.se',
    'svedala.se',
    'tingsryd.se',
    'tomelilla.se',
    'trelleborg.se',
    'uppvidinge.se',
    'varberg.se',
    'vaxjo.se',
    'vellinge.se',
    'ystad.se',
    'sprinta.se'
  ];

  const onSubmit = async (data: LoginFormValues) => {
    try {
      const response = await authService.login(data.email, data.password, loggedUsers);
      if(response.data.firstLogin){
        setUserData(response.data);
        setShowTwoFactorDialog(true);
        
        if(response.data.user.phoneNumber){
          setTwoFactorStep("method");
        } else {
          await handleRequestCode(response.data.user.userID);
          setTwoFactorStep("code");
        }
      }
      else{
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      }
    } catch (err: any) {
      setErrorMessage("Felaktig e-post eller lösenord.");
    }
  };

  const handleRequestCode = async (userID?: string) => {
    setIsRequestingCode(true);
    setTwoFactorError(null);
    
    try {
      const userId = userID || userData.user.userID;
      await generateTwoFactorCode(userId, twoFactorMethod);
      
      setTwoFactorStep("code");
    } catch (error) {
      setTwoFactorError("Kunde inte skicka kod. Försök igen.");
    } finally {
      setIsRequestingCode(false);
    }
  };

  const onSubmitTwoFactor = async (data: TwoFactorFormValues) => {
    try {
      setTwoFactorError(null);

      await authService.verifyCode(userData.user.userID, data.code).then((res) => {
        setUser(res.data);
        localStorage.setItem('user', JSON.stringify(res.data));
        const updatedLoggedUsers = loggedUsers + userData.user.userID + "," + data.code + ";";
        setLoggedUsers(updatedLoggedUsers);
        localStorage.setItem("loggedUsers", updatedLoggedUsers);
      });
    } catch (err: any) {
      setTwoFactorError("Ogiltig kod. Försök igen.");
    }
  };

  const handleTwoFactorClose = () => {
    setShowTwoFactorDialog(false);
    setTwoFactorStep("method");
    setTwoFactorMethod("email");
    setTwoFactorError(null);
    setUserData(null);
  };

  const onSubmitAccountRequest = async (data: EmailFormValues) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      
      const email = data.email.trim();
      const domain = email.split('@')[1];
      if (!approvedDomains.find(d => d === domain)) {
        setErrorMessage("Domain not approved");
        return;
      }
      console.log(email);
      console.log(domain);
      await requestEmailToken(data.email);
      setSuccessMessage("En ansökan om konto skickas inom kort till din e-postadress.");
      resetAccountRequest({ email: "" });
      setAccountRequestSent(true);
      
      console.log("Account request for:", data.email);
    } catch (err: any) {
      setErrorMessage("Kunde inte skicka ansökan. Försök igen senare.");
    }
  };

  const onSubmitPasswordReset = async (data: EmailFormValues) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);

      await resetPasswordRequest(data.email, 1);
      
      // TODO: Call your API service for password reset
      // await authService.requestPasswordReset(data.email);
      
      setSuccessMessage("Instruktioner för återställning har skickats till din e-post.");
      console.log("Password reset for:", data.email);
    } catch (err: any) {
      setErrorMessage("Kunde inte skicka återställningslänk. Försök igen senare.");
    }
  };

  const handleBackToLogin = () => {
    setViewState("login");
    setErrorMessage(null);
    setSuccessMessage(null);
    setAccountRequestSent(false);
  };

  useEffect(() => {
    if (viewState === "accountRequest") {
      resetAccountRequest({ email: "" });
      setAccountRequestSent(false);
      setErrorMessage(null);
      setSuccessMessage(null);
    }
  }, [viewState]);

  return (
    <div className="bg-[#ebebeb]">
      <div className="flex justify-center h-[30vh] min-h-[200px] bg-gradient-to-r from-orange-700 to-yellow-500 rounded-b w-full text-white">
        <div className="text-center max-w-3xl mt-10">
          <h1 className="text-3xl">Välkommen till UPPHANDLAT</h1>
          <p className="text-sm mt-5 px-6">
            Upphandlat är ett verktyg för avrop från gemensamt upphandlade avtal.
            Verktyget tillhandahålles av Skånes Kommuner och kan användas fritt av de kommuner
            som deltar i de gemensamma upphandlingar och avtal som hanteras i verktyget,
            samt av de leverantörer vars tjänster kan avropas genom verktyget.
          </p>
        </div>
      </div>
      <div className="-mt-10">
        <div className="bg-white gap-5 flex flex-col rounded shadow-md w-[500px] m-auto overflow-y-auto p-5">
          
          {/* Login View */}
          {viewState === "login" && (
            <>
              <div className="flex flex-col">
                <Subtitle1>Logga in i Upphandlat</Subtitle1>
                <Caption1>
                  Du kan behöva bekräfta med tillfällig kod som du får via mail/sms
                </Caption1>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <Field label="E-post" required validationMessage={errors.email?.message}>
                  <Input {...register("email", { required: "E-post krävs" })} />
                </Field>

                <Field label="Lösenord" required validationMessage={errors.password?.message}>
                  <Input type="password" {...register("password", { required: "Lösenord krävs" })} />
                </Field>

                {errorMessage && (
                  <Caption1 className="text-red-500">{errorMessage}</Caption1>
                )}

                <Button appearance="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Loggar in..." : "Logga in"}
                </Button>
              </form>

              <Divider className="my-2" />

              <div className="flex justify-center gap-2">
                <Button appearance="secondary" onClick={() => setViewState("passwordReset")}>Begär nytt lösenord</Button>
                <Button onClick={() => setSupportDrawerOpen(true)}>Kontakta support</Button>
              </div>

              <div className="flex justify-center">
                <Button onClick={() => setViewState("accountRequest")}>Ansök om konto</Button>
              </div>
            </>
          )}

          {/* Account Request View */}
          {viewState === "accountRequest" && (
            <>
              <div className="flex flex-col">
                <Subtitle1>Ansök om konto</Subtitle1>
                <Caption1>
                  Fyll i din e-postadress så kontaktar vi dig för att skapa ett konto
                </Caption1>
              </div>

              <form onSubmit={handleSubmitAccountRequest(onSubmitAccountRequest)} className="flex flex-col gap-4">
                <Field label="E-post" required validationMessage={accountRequestErrors.email?.message}>
                  <Input 
                    {...registerAccountRequest("email", { 
                      required: "E-post krävs",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Ogiltig e-postadress"
                      }
                    })} 
                    placeholder="din.epost@exempel.se"
                  />
                </Field>

                {errorMessage && (
                  <Caption1 className="text-red-500">{errorMessage}</Caption1>
                )}

                {successMessage && (
                  <Caption1 className="text-green-600">{successMessage}</Caption1>
                )}

                <Button appearance="primary" type="submit" disabled={isSubmittingAccountRequest || accountRequestSent}>
                  {isSubmittingAccountRequest ? "Skickar ansökan..." : "Skicka ansökan"}
                </Button>

                <Button appearance="secondary" onClick={handleBackToLogin}>
                  Tillbaka till inloggning
                </Button>
              </form>
            </>
          )}

          {/* Password Reset View */}
          {viewState === "passwordReset" && (
            <>
              <div className="flex flex-col">
                <Subtitle1>Begär nytt lösenord</Subtitle1>
                <Caption1>
                  Ange din e-postadress så skickar vi instruktioner för att återställa ditt lösenord
                </Caption1>
              </div>

              <form onSubmit={handleSubmitPasswordReset(onSubmitPasswordReset)} className="flex flex-col gap-4">
                <Field label="E-post" required validationMessage={passwordResetErrors.email?.message}>
                  <Input 
                    {...registerPasswordReset("email", { 
                      required: "E-post krävs",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Ogiltig e-postadress"
                      }
                    })} 
                    placeholder="din.epost@exempel.se"
                  />
                </Field>

                {errorMessage && (
                  <Caption1 className="text-red-500">{errorMessage}</Caption1>
                )}

                {successMessage && (
                  <Caption1 className="text-green-600">{successMessage}</Caption1>
                )}

                <Button appearance="primary" type="submit" disabled={isSubmittingPasswordReset}>
                  {isSubmittingPasswordReset ? "Skickar..." : "Skicka återställningslänk"}
                </Button>

                <Button appearance="secondary" onClick={handleBackToLogin}>
                  Tillbaka till inloggning
                </Button>
              </form>
            </>
          )}

        </div>
      </div>

      {/* Two-Factor Authentication Dialog */}
      <Dialog
        modalType="alert" 
        open={showTwoFactorDialog} 
        onOpenChange={(_, data) => !data.open && handleTwoFactorClose()}
      >
        <DialogSurface style={{ minWidth: '400px' }}>
          <DialogTitle
            action={
              <Button
                shape="circular"
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={handleTwoFactorClose}
                aria-label="Stäng"
                style={{ marginTop: '-50px' }}
              />
            }
          >
            Tvåfaktorsautentisering
          </DialogTitle>
          
          <DialogContent>
            {twoFactorStep === "method" && (
              <div className="flex flex-col gap-4">
                <Text>Välj hur du vill ta emot din verifieringskod:</Text>
                
                <RadioGroup 
                  value={twoFactorMethod}
                  onChange={(_, data) => setTwoFactorMethod(data.value)}
                >
                  <Radio value="1" label="E-post" />
                  <Radio value="2" label="SMS" />
                </RadioGroup>

                {twoFactorError && (
                  <Caption1 className="text-red-500">{twoFactorError}</Caption1>
                )}
              </div>
            )}

            {twoFactorStep === "code" && (
              <div className="flex flex-col gap-4">
                <Text>
                  En verifieringskod har skickats till din {twoFactorMethod === "1" ? "e-post" : "telefon"}. 
                  Ange koden nedan:
                </Text>
                
                <form onSubmit={handleSubmitTwoFactor(onSubmitTwoFactor)} className="flex flex-col gap-4">
                  <Field 
                    label="Verifieringskod" 
                    validationMessage={twoFactorErrors.code?.message}
                  >
                    <Input
                      autoFocus
                      {...registerTwoFactor("code", { 
                        required: "Verifieringskod krävs",
                        minLength: { value: 6, message: "Koden måste vara 6 siffror" },
                        maxLength: { value: 6, message: "Koden måste vara 6 siffror" },
                        pattern: { value: /^\d{6}$/, message: "Koden måste innehålla endast siffror" }
                      })} 
                      placeholder="123456"
                      maxLength={6}
                    />
                  </Field>

                  {twoFactorError && (
                    <Caption1 className="text-red-500">{twoFactorError}</Caption1>
                  )}
                </form>
              </div>
            )}
          </DialogContent>

          <DialogActions className="mt-4">
            <Button appearance="secondary" onClick={handleTwoFactorClose}>
              Avbryt
            </Button>
            
            {twoFactorStep === "method" && (
              <Button 
                appearance="primary" 
                onClick={() => handleRequestCode()}
                disabled={isRequestingCode}
              >
                {isRequestingCode ? (
                  <>
                    <Spinner size="tiny" className="mr-2" />
                    Skickar kod...
                  </>
                ) : (
                  "Skicka kod"
                )}
              </Button>
            )}
            
            {twoFactorStep === "code" && (
              <Button 
                appearance="primary" 
                onClick={handleSubmitTwoFactor(onSubmitTwoFactor)}
                disabled={isSubmittingTwoFactor}
              >
                {isSubmittingTwoFactor ? "Verifierar..." : "Verifiera"}
              </Button>
            )}
          </DialogActions>
        </DialogSurface>
      </Dialog>
      <SupportDrawer open={supportDrawerOpen} onClose={() => setSupportDrawerOpen(false)} />
    </div>
  );
};

export default Login;
